import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import React from 'react';
import { v4 as uuid4 } from 'uuid';
import type { TIcon } from '@datatype/type';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import SideContainer from '@component/common/architecture/side-container';
import Avatar from '@component/common/avatar';
import Gap from '@component/common/gap';
import ImageBlock from '@component/common/block/image-block';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import gridgioLogo from '@asset/image/gridgio-logo-white.svg';
import { createProjectPath, GRIDGIO_PATH } from '@constant/gridgio/route-path';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { useTypedDispatch } from '@hook/common/useStore';
import { useFindMe } from '@hook/auth/useAuth';
import { actions as authActions } from '@store/slice/auth-slice';

const SIDE_TAB_DESIGN = {
  width: size.GRIDGIO_SIDE_WIDTH,
  align: 'up',
  backgroundColor: color.BLACK_2,
} as const;

const MenuItemsContainer = styled.nav`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: ${size.MARGIN_2};
`;

const MenuItemContainer = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  align-items: center;
  justify-content: center;
`;

const Spacer = styled.div`
  flex: 1;
`;

const AvatarContainer = styled.div`
  height: 40px;
`;

const AVATAR_DESIGN = {
  text: { size: '14px' },
  avatarSize: '40px',
} as const;

const SideTab: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mainMenu = pathname.split('/')[2];
  const dispatch = useTypedDispatch();
  const me = useFindMe();
  const initial = me?.data?.username?.[0]?.toUpperCase() ?? '';
  const menus: { id: 'home' | 'project' | 'lab' | 'setting'; label: string; icon: TIcon; handler?: () => void }[] = [
    {
      id: 'home',
      label: '홈',
      icon: 'home',
      handler: () => navigate(GRIDGIO_PATH.HOME_BEGIN_PATH),
    },
    { id: 'project', label: '프로젝트', icon: 'building', handler: () => navigate(createProjectPath('home')) },
    {
      id: 'lab',
      label: '실험실',
      icon: 'flask',
      handler: () => navigate(GRIDGIO_PATH.LAB_HOME_PATH),
    },
    {
      id: 'setting',
      label: 'GridgIO 설정',
      icon: 'setting',
      handler: () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' })),
    },
  ];
  const MenuItems = menus.map((menu) => (
    <MenuItemContainer key={uuid4()} title={menu.label} onClick={menu.handler}>
      <IconBlock
        icon={menu.icon}
        design={{
          box: { width: '40px', height: '40px', event: { cursor: menu.handler ? 'pointer' : 'auto' } },
          icon: { size: 'medium', color: mainMenu === menu.id ? color.WHITE_1 : color.GRAY_1 },
        }}
      />
      <TextBlock
        text={menu.id === 'setting' ? '설정' : menu.label}
        design={{ text: { size: 'tiny', color: mainMenu === menu.id ? color.WHITE_1 : color.GRAY_1 } }}
      />
    </MenuItemContainer>
  ));

  return (
    <>
      <SideContainer design={SIDE_TAB_DESIGN}>
        <Gap height={size.MARGIN_1} />
        <ImageBlock
          src={gridgioLogo}
          alt='GridgIO 홈'
          design={{ box: { event: { cursor: 'pointer' } }, image: { width: '40px', height: '40px' } }}
          click={{ handler: () => navigate(GRIDGIO_PATH.CHECKPOINT_PATH) }}
        />
        <Gap height={size.MARGIN_3} />
        <MenuItemsContainer aria-label='GridgIO 메뉴'>{MenuItems}</MenuItemsContainer>
        <Spacer />
        <AvatarContainer>
          <Avatar name={initial} design={AVATAR_DESIGN} onClick={() => dispatch(authActions.openMyPage())} />
        </AvatarContainer>
        <Gap height={size.MARGIN_1} />
      </SideContainer>
    </>
  );
};

export default SideTab;
